import type { NewCoreIconProps } from '@atlaskit/icon';
import {
	createEditorToolbarActions,
	type ManifestEditorToolbarActions,
} from '@atlaskit/native-embeds-common';
import {
	type Experience,
	type ExperienceManifest,
	experienceManifestId,
	type ExperienceManifestId,
	type LoadingComponentProps,
	type NativeEmbedChildCommand,
} from '@atlassian/native-embeds-core/types';

import { dbMoreItems, dbSharedManifestFields, dbSharedUiConfig, dbToolbarItems } from './shared';

export const DB_EXPERIENCE_MANIFEST_ID: ExperienceManifestId = experienceManifestId('database');

const isDatabaseUrl = (url: string): boolean => {
	return url.match(/\/wiki\/spaces\/~?[\d\w]+\/database\/\d+(\?.*)?$/) !== null;
};

const isDatabaseEntryUrl = (url: string): boolean => {
	try {
		return isDatabaseUrl(url) && new URL(url).searchParams.has('entryId');
	} catch {
		return false;
	}
};

const isDatabaseNonEntryUrl = (url: string): boolean => {
	return isDatabaseUrl(url) && !isDatabaseEntryUrl(url);
};

/**
 * Experience manifest for a regular DB embed (no entryId in URL).
 * Register with getNativeEmbedFacade().registerExperienceManifest(DBExperienceManifest),
 * then call getNativeEmbedFacade().createExperienceForUrl(url) to obtain an Experience for EmbedFrameUI.
 */
export const DBExperienceManifest: {
	readonly handleChildCommands: (
		_experience: Experience,
		_command: NativeEmbedChildCommand,
	) => void;
	readonly id: ExperienceManifestId;
	readonly isActive: () => true;
	readonly isUrlMatch: (url: string) => boolean;
	readonly lockResizeAspectRatio: () => false;
	readonly parameterDefaults: {
		readonly alwaysShowTitle: true;
	};
	readonly shouldAutoConvertToNativeEmbed: true;
	readonly supportedFeatures: Set<string>;
	readonly transformUrl: (url: string, _isFullScreen?: boolean) => string;
	readonly uiConfig: {
		readonly appearance: 'minimal';
		readonly editorToolbarActions: ManifestEditorToolbarActions<any>;
		// eslint-disable-next-line @typescript-eslint/ban-types
		readonly editorToolbarHandlers: {};
		readonly renderLoadingComponent: (props: LoadingComponentProps) => JSX.Element;
		readonly showBorder: false;
		readonly showHeader: false;
		readonly titleIcon: {
			(props: NewCoreIconProps): JSX.Element;
			displayName: string;
		};
	};
} = {
	...dbSharedManifestFields,
	id: DB_EXPERIENCE_MANIFEST_ID,
	isUrlMatch: isDatabaseNonEntryUrl,
	parameterDefaults: {
		alwaysShowTitle: true,
	},
	uiConfig: {
		...dbSharedUiConfig,
		editorToolbarActions: createEditorToolbarActions({
			items: [...dbToolbarItems],
			moreItems: dbMoreItems,
		}),
	},
} as const satisfies ExperienceManifest;
